import { endOfDay, format, set } from "date-fns";
import React, { FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

//components
import Button from "../../../../../../components/Button";
import Icon from "../../../../../../components/Icon/Icon";
import InputCheckbox from "../../../../../../components/Inputs/Checkbox";
import DatePicker from "../../../../../../components/Inputs/DatePicker";
import Input from "../../../../../../components/Inputs/InputText";
import FormModal from "../../../../../../components/Modal/Templates/FormModal";
import OutlookPicker from "../../../../../../components/OutlookPicker";
import Spacer from "../../../../../../components/Spacer";
import Typography from "../../../../../../components/Text";
import TruncatedText from "../../../../../../components/Text/TruncatedText";
import { TextEditor } from "../../../../../../components/TextEditor";
import Tooltip from "../../../../../../components/Tooltip";

import {
	useGoalDetails,
	useGoalHistory,
	useMutateGoalDetails,
	useRbacObjectPermissions,
} from "../../../../../../api/hooks";
//hooks
import useForm from "../../../../../../hooks/useForm";
import usePageWidthBreakpoint from "../../../../../../hooks/usePageWidthBreakpoint";
//hooks
import useSearchQueryParams from "../../../../../../hooks/useSearchQueryParams";
import useToast from "../../../../../../hooks/useToast";

//utils
import { Goal, OutlookColor } from "../../../../../../api/services";

import { parseGoalPermissionsForUser } from "../../../utils";
import { getProgressLabels } from "../../SingleGoalInfo/utils";
import { formSchema } from "./utils";

// config
import Config from "../../../config";
//css
import css from "./UpdateProgressModal.module.scss";

const UpdateProgressModal = () => {
	const { isMobile } = usePageWidthBreakpoint();
	const toast = useToast();
	const [validation, setValidation] = useState<Record<string, any> | null>(null);
	const { goal_id: goalId } = useParams<{ goal_id: string }>();

	const { data: permissions } = useRbacObjectPermissions("goal", goalId || "");

	const { data, refetch: refetchGoal } = useGoalDetails({ goalID: goalId || "" });
	const goal = data ? data.goal : ({} as Goal);

	const { refetch: refetchGoalProgress } = useGoalHistory({
		goalID: goalId || "",
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
	});

	const { refetch: refetchGoalActivity } = useGoalHistory({
		goalID: goalId || "",
		sortDirection: "desc",
		sortName: "_id",
	});

	const { userCanEditValue, userCanEditStatus } = parseGoalPermissionsForUser(permissions);

	const {
		queryParams: { modal, initialGoalProgressValue },
		handleQueryChange,
	} = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const isOpen = modal === "updateProgress";
	const handleClose = () => handleQueryChange({ modal: undefined, initialGoalProgressValue: undefined });

	const { state, updateForm, validate, resetForm, setInitialState } = useForm({
		schema: formSchema,
		disableWarning: !isOpen,
	});

	const { mutate, isPending } = useMutateGoalDetails(goal?._id, {
		onSuccess: () => {
			refetchGoal();
			refetchGoalActivity();
			refetchGoalProgress();
			handleClose();
		},
		onError: () => {
			toast("Something went wrong, please try again", {
				type: "error",
				size: "small",
			});
		},
	});

	useEffect(() => {
		setInitialState({
			goalValue: goal?.value,
			incrementValue: null,
			isIncrement: false,
			asOfDate: new Date(),
			message: "",
			outlook: { ...goal?.color, id: goal?.color?._id || null },
		});
	}, []);

	const handleChange = (value: any, name: string) => {
		updateForm({
			[name]: value,
		});
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const validator = validate();
		if (validator.isValid) {
			const currentTime = new Date();
			const newValue =
				state?.isIncrement && state?.incrementValue ? goal?.value + state.incrementValue : state?.goalValue;
			const newColor = state?.outlook && state.outlook?.id !== "none" ? state?.outlook?.id : null;
			const newDate = state?.asOfDate
				? set(state.asOfDate, {
						hours: currentTime.getHours(),
						minutes: currentTime.getMinutes(),
						seconds: currentTime.getSeconds(),
					}).toISOString()
				: null;
			const updates: Array<{ field: string; value: any }> = [];
			if (newValue || newValue === 0) {
				updates.push({ field: "value", value: newValue });
			}

			if (state?.message) {
				updates.push({ field: "message", value: state?.message });
			}

			if (newDate) {
				updates.push({ field: "as_of", value: newDate });
			}

			if (newColor) {
				updates.push({ field: "color", value: newColor });
			} else if (goal?.color) {
				updates.push({ field: "color", value: null });
			}
			mutate(updates);
		} else {
			setValidation(validator.errors);
		}
	};

	useEffect(() => {
		if (isOpen) {
			setValidation(null);
			resetForm();
			handleChange(
				typeof initialGoalProgressValue === "number" ? initialGoalProgressValue : goal?.value,
				"goalValue"
			);
			handleChange({ ...goal?.color, id: goal?.color?._id || null }, "outlook");
		} else {
			// this is needed to fix most modal height flickering due to validation still being present
			// we want to clear the validatino on close, but only after the modal has disapeared
			setTimeout(() => {
				setValidation(null);
			}, 300);
		}
	}, [isOpen]);

	const getDateValidationMessage = () => {
		let validationMessage = "";

		if (!state?.asOfDate) {
			validationMessage = "Please enter a date";
		} else if (state?.asOfDate && state?.asOfDate > endOfDay(new Date())) {
			validationMessage = "Date cannot be in the future";
		}

		return validationMessage;
	};

	const labels = getProgressLabels({
		value: goal?.value,
		units: goal?.units,
		truncateSubtextChars: state?.isIncrement ? 10 : undefined,
	});
	const targetLabels = getProgressLabels({ value: goal?.target, units: goal?.units });
	const incrementPostLabels = getProgressLabels({
		value: Number(state?.incrementValue || 0) + (goal?.value || 0),
		units: goal?.units,
		truncateSubtextChars: 10,
		decimalPlacesOverride: 2,
	});

	return (
		<FormModal
			closeOnClickOutside={false}
			isOpen={isOpen}
			handleClose={handleClose}
			onSubmit={handleSubmit}
			title={
				<div className={css.title}>
					<Typography size="sub-heading" weight="semi-bold">
						Update Progress
					</Typography>
					<TruncatedText
						maxWidth={isMobile ? 325 : 448}
						text={`Target: ${targetLabels?.mainText || ""} ${targetLabels?.subText || ""}`}
						color="neutral-4"
						size="paragraph-2"
						weight="regular"
						maxLines={2}
						canShowMore={false}
					/>
				</div>
			}
			buttons={[
				<Button key="cancel-update-progress" testid="cancel-update-progress" onClick={handleClose}>
					Cancel
				</Button>,
				<Button
					disabled={!userCanEditValue}
					key="update-progress"
					testid="update-progress"
					color="green"
					submit
					isRequesting={isPending}
				>
					Update Progress
				</Button>,
			]}
		>
			<div className={css.updateProgressFormContent}>
				<div>
					{state?.isIncrement ? (
						<div className={css.goalValueContainer}>
							<div className={css.goalValueInput}>
								<Input
									autoFocus
									value={
										!isNaN(Number(state?.incrementValue)) &&
										state?.incrementValue !== null &&
										state?.incrementValue !== undefined
											? state.incrementValue
											: ""
									}
									onChange={(value) => {
										handleChange(parseFloat(value), "incrementValue");
									}}
									label="Update Goal Value"
									type="number"
									placeholder="Add value"
									testId="goalValue"
									errorMessage={validation?.incrementValue?.message && "Please enter a valid number"}
									animatedError={isOpen}
								/>
							</div>
							<div className={css.goalValueCalculation}>
								<Typography
									size="paragraph-3"
									weight="regular"
								>{` + ${labels.mainText} = ${incrementPostLabels.mainText}`}</Typography>
							</div>
						</div>
					) : (
						<Input
							autoFocus
							value={
								!isNaN(Number(state?.goalValue)) &&
								state?.goalValue !== null &&
								state?.goalValue !== undefined
									? state.goalValue
									: ""
							}
							onChange={(value) => {
								handleChange(parseFloat(value), "goalValue");
							}}
							label="Update Goal Value"
							type="number"
							placeholder={`${labels.mainText} ${labels.subText}`}
							testId="goalValue"
							errorMessage={validation?.goalValue?.message && "Please enter a valid number"}
							animatedError={isOpen}
						/>
					)}
				</div>
				<div className={css.checkboxRow}>
					<InputCheckbox
						value={state?.isIncrement}
						onChange={() => handleChange(!state?.isIncrement, "isIncrement")}
					/>
					<div className={css.checkboxRowLabel}>
						<Typography size="paragraph-3" weight="semi-bold">
							Increment Value
						</Typography>
						<Tooltip
							placement="right"
							title="Increment Value"
							message="Increment value will add this value to the current value of this goal. If you do not check this box, this value will replace the current value."
							revealed
						>
							<Icon icon={faCircleInfo} size="x-small" color="neutral-7" />
						</Tooltip>
					</div>
				</div>
				<div>
					<Typography size="paragraph-3" weight="semi-bold">
						Message
					</Typography>
					<Spacer height={1} />
					<TextEditor
						initialValue=""
						isRichText={false}
						placeholder="Add Message"
						onChange={(value) => handleChange(value, "message")}
						id="message-update goal progress"
						className={css.textEditor}
					/>
				</div>
				<div>
					<DatePicker
						value={state?.asOfDate || ""}
						onChange={(value) => handleChange(value, "asOfDate")}
						label="As Of Date"
						placeholder={format(new Date(), "MMM dd, yyyy")}
						inputId="asOfDate"
						errorMessage={getDateValidationMessage()}
						animatedError={isOpen}
						hasIcon={true}
					/>
				</div>
				{userCanEditStatus && (
					<div>
						<OutlookPicker
							label="Outlook"
							value={state?.outlook as OutlookColor}
							onChange={(value: OutlookColor) => handleChange(value, "outlook")}
							isClearable
							maxMenuHeight={100}
							placeholder="Not Selected"
						/>
					</div>
				)}
			</div>
		</FormModal>
	);
};

export default UpdateProgressModal;
